import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import TextField from "./components/TextField";
import MessageBox from "./components/MessageBox";
import { AiOutlineLoading } from "react-icons/ai";
import { HiInformationCircle } from "react-icons/hi";
import PasswordField from "./components/PasswordField";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/context/AdminContext";

const Login = () => {
  const { adminLogin, sysAdmin } = useAdminContext();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [succes, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  const responses = (err, message) => {
    setError(err);
    setSuccess(!err);
    setLoading(false);
    setErrorMessage(message);
  };

  const sendLoginRequest = async (e) => {
    e.preventDefault(); // Prevents HTML form submission refresh
    setLoading(true);
    setErrorMessage(""); // Clear previous errors
    setError(false);

    // Validation
    if (!email || email === "") {
      responses(true, "Email address is required");
      return;
    } else if (!password || password === "") {
      responses(true, "Password is required");
      return;
    }

    try {
      const response = await adminLogin({
        login: email,
        password: password,
      });

      // Defensive check: Ensure response exists before accessing properties
      if (!response) {
        throw new Error(
          "No response from server. Please check your connection.",
        );
      }

      const { message, status } = response;

      if (status !== 200 && status !== 201) {
        // Handle API logic errors (e.g. wrong password)
        responses(true, message);
        return;
      } else {
        // Success case is usually handled by the redirect via sysAdmin check
        setLoading(false);
      }
    } catch (err) {
      // Handle network errors or crashes
      // console.error("Login Error:", err);
      responses(true, "Something went wrong. Please try again later.");
    }
  };

  if (sysAdmin !== null) {
    return <Navigate replace to="/" />;
  }

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-white text-gray-900">
      <div className="mx-auto grid min-h-screen w-full max-w-7xl grid-cols-1 lg:grid-cols-2">
        {/* Left column: Brand & Story (desktop only) */}
        <aside className="relative hidden lg:block">
          <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_-200px_-200px,rgba(16,185,129,0.08),transparent_60%)]" />
          <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
            <div className="flex items-center gap-3">
              <img
                src={dosee_logo}
                alt="Dósee Africa logo"
                className="h-10 w-auto"
              />
              <div className="text-sm">
                <p className="font-semibold">Dosee Africa • Admin</p>
                <p className="text-xs opacity-80">
                  Secure access to manage the platform
                </p>
              </div>
            </div>

            <div className="mt-12 space-y-8 pr-4">
              <h2 className="text-3xl font-bold tracking-tight">
                Simplifying Tourism Across Africa
              </h2>
              <p className="text-sm leading-6 text-gray-700 max-w-prose">
                Welcome back! Use your administrator credentials to curate,
                approve, and monitor experiences, hosts, and bookings across the
                continent.
              </p>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                    Our Mission
                  </h3>
                  <p className="text-sm text-gray-700">
                    To simplify tourism in Africa and give access to authentic
                    and unforgettable African experiences while preserving the
                    continent's rich natural heritage.
                  </p>
                </section>

                <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                    Our Vision
                  </h3>
                  <p className="text-sm text-gray-700">
                    Connect the world to the wonders of Africa with hassle-free,
                    sustainable vacations and events—fostering conservation and
                    community development.
                  </p>
                </section>

                <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:col-span-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                    Our Objectives
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    <li>
                      Enhance customer experiences with curated, culturally
                      immersive adventures.
                    </li>
                    <li>
                      Promote sustainability and nature conservation in every
                      operation.
                    </li>
                    <li>
                      Empower local communities through responsible
                      partnerships.
                    </li>
                    <li>
                      Embrace innovation to personalize and streamline bookings.
                    </li>
                    <li>
                      Build trust and transparency with travelers and partners.
                    </li>
                    <li>
                      Expand reach to showcase Africa’s diverse cultural and
                      natural richness.
                    </li>
                    <li>
                      Invest in team development, creativity, and well-being.
                    </li>
                  </ul>
                </section>
              </div>

              <div className="flex items-center gap-3 text-xs text-gray-600">
                <span
                  className="inline-flex h-2 w-2 rounded-full bg-emerald-500"
                  aria-hidden="true"
                />
                All systems operational • Encrypted sign-in
              </div>
            </div>

            <footer className="mt-10 text-xs text-gray-500">
              © {new Date().getFullYear()} Dósee Africa. Admin access only.
            </footer>
          </div>
        </aside>

        {/* Right column: Form */}
        <main className="relative flex items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
          <div className="relative w-full max-w-md">
            <div
              className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-emerald-200/40 via-amber-200/40 to-purple-200/40 blur-2xl"
              aria-hidden="true"
            />
            <div className="relative rounded-3xl border border-gray-200 bg-white p-6 sm:p-8 shadow-xl">
              <div className="mb-6 flex items-center justify-center lg:hidden">
                <img
                  src={dosee_logo}
                  alt="Dósee Africa logo"
                  className="h-12 w-auto"
                />
              </div>

              <header className="mb-6">
                <h1 className="text-center text-2xl font-bold tracking-tight">
                  Admin Portal
                </h1>
                <p className="mt-2 text-center text-sm text-gray-700">
                  Sign in to continue managing experiences, hosts, and bookings.
                </p>
              </header>

              <form onSubmit={sendLoginRequest} className="w-full">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">
                      Email Address
                    </label>
                    <TextField
                      value={email}
                      type="email"
                      title="Email Address"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Use your work email registered for admin access.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">
                      Password
                    </label>
                    <PasswordField
                      title="Password"
                      password={password}
                      setError={setError}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(false);
                        setSuccess(false);
                      }}
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        Remember me on this device
                      </label>
                    </div>
                  </div>

                  <MessageBox
                    error={error}
                    success={succes}
                    message={message}
                    onClick={() => {
                      setError(false);
                      setSuccess(false);
                    }}
                  />
                </div>

                <div className="mt-6">
                  <button
                    type={loading ? "button" : "submit"}
                    className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  >
                    <span className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
                    {loading ? (
                      <AiOutlineLoading className="text-lg animate-spin" />
                    ) : (
                      <span>Connect</span>
                    )}
                  </button>
                </div>

                <div className="mt-4 flex items-start gap-2 text-[11px] text-gray-600">
                  <HiInformationCircle className="mt-0.5 text-base" />
                  <p>
                    Always log out when stepping away. By continuing, you agree
                    to our
                    <Link to="/terms" className="mx-1 underline">
                      Terms of Use
                    </Link>
                    and
                    <Link to="/privacy" className="ml-1 underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <Link
                    to="/signup"
                    className="text-xs font-bold text-emerald-700 underline hover:text-emerald-600"
                  >
                    No Account? Create a company account
                  </Link>
                  <p className="text-[11px] text-gray-600">
                    Need help?{" "}
                    <a
                      href="mailto:support@doseeafrica.com"
                      className="underline"
                    >
                      Contact support
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
